import React, { useState, useEffect, useRef, createContext, useContext, ReactNode } from 'react';
import { Outlet, Link, useLocation, Navigate, useNavigate, useOutletContext } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ThemeToggle } from '../ThemeToggle';
import { StudioXCredit } from '../StudioXCredit';
import {
  LayoutDashboard,
  Users,
  Building2,
  FileText,
  Target,
  BarChart3,
  LogOut,
  Menu,
  X,
  Shield,
  FileDown,
  AlertCircle,
  ClipboardList,
  GitBranch,
  User,
  UserCog,
  ChevronUp,
  Edit3,
  ArrowLeft,
} from 'lucide-react';

// Type for header action
type HeaderAction = ReactNode | null;

// Context type for outlet
type AdminLayoutContextType = {
  setHeaderAction: (action: HeaderAction) => void;
  setPageTitle: (title: string | null) => void;
  setPageSubtitle: (subtitle: string | null) => void;
  setBackTo: (path: string | null) => void;
};

// Custom hook to use in child components
export function useAdminLayoutContext() {
  return useOutletContext<AdminLayoutContextType>();
}

export function AdminLayout() {
  const { user, profile, loading, signOut, isAdmin, isSuperAdmin } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [headerAction, setHeaderAction] = useState<HeaderAction>(null);
  const [pageTitle, setPageTitle] = useState<string | null>(null);
  const [pageSubtitle, setPageSubtitle] = useState<string | null>(null);
  const [backTo, setBackTo] = useState<string | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen]);


  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-600">Laster...</div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Only superadmins and editors can access admin panel
  if (profile && profile.role !== 'superadmin' && profile.role !== 'editor') {
    return <Navigate to="/assessment/start" replace />;
  }

  // Helper to check if current user is superadmin
  const isSuperadminUser = profile?.role === 'superadmin';

  // Show warning if profile is missing (shouldn't happen after login)
  const profileWarning = !profile && (
    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <AlertCircle className="h-5 w-5 text-yellow-400" />
        </div>
        <div className="ml-3">
          <p className="text-sm text-yellow-700">
            Profile kunne ikke lastes. Kontakt support.
          </p>
        </div>
      </div>
    </div>
  );

  const navigation = [
    { name: 'Dashboard', href: '/admin', icon: LayoutDashboard, roles: ['superadmin', 'editor'] },
    { name: 'Brukere', href: '/admin/users', icon: Users, roles: ['superadmin', 'editor'] },
    { name: 'Kunder', href: '/admin/companies', icon: Building2, roles: ['superadmin'] },
    { name: 'Avdelinger', href: '/admin/departments', icon: ClipboardList, roles: ['superadmin'] },
    { name: 'Symptomgrupper', href: '/admin/symptom-groups', icon: FileText, roles: ['superadmin', 'editor'] },
    { name: 'Tilnærminger', href: '/admin/approaches', icon: Target, roles: ['superadmin', 'editor'] },
    { name: 'Påvirkningsregler', href: '/admin/influence-rules', icon: GitBranch, roles: ['superadmin', 'editor'] },
    { name: 'Statistikk', href: '/admin/statistics', icon: BarChart3, roles: ['superadmin', 'editor'] },
    { name: 'Import/Eksport', href: '/admin/import-export', icon: FileDown, roles: ['superadmin'] },
  ];

  // Filter navigation based on user role
  const filteredNavigation = navigation.filter(item =>
    item.roles.includes(profile?.role || '')
  );

  const isActive = (href: string) => {
    if (href === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(href);
  };

  const handleSignOut = async () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
      await signOut();
      window.location.href = '/login';
    } catch (err) {
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/login';
    }
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        flex flex-col bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300
        fixed inset-y-0 left-0 z-50 md:relative md:z-auto md:translate-x-0
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        ${sidebarOpen ? 'w-64' : 'w-64 md:w-16'}
      `}>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className={`flex items-center space-x-3 ${!sidebarOpen && 'justify-center'}`}>
              <Shield className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              {sidebarOpen && (
                <span className="text-xl font-bold text-gray-900 dark:text-white">Admin</span>
              )}
            </div>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
            >
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Back to assessment link */}
        <div className="px-4 pt-4">
          <Link
            to="/assessment/start"
            className={`flex items-center gap-2 text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 transition-colors ${!sidebarOpen && 'justify-center'}`}
            title={!sidebarOpen ? 'Tilbake til vurdering' : undefined}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {sidebarOpen && <span className="text-sm font-medium">Tilbake til vurdering</span>}
          </Link>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {filteredNavigation.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center space-x-3 px-3 py-2 rounded-md transition-colors ${
                  active
                    ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                } ${!sidebarOpen && 'justify-center'}`}
                title={!sidebarOpen ? item.name : undefined}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                {sidebarOpen && <span className="font-medium">{item.name}</span>}
              </Link>
            );
          })}
        </nav>

        <div ref={userMenuRef} className="p-4 border-t border-gray-200 dark:border-gray-700 relative">
          {/* User Menu Dropdown */}
          {userMenuOpen && sidebarOpen && (
            <div className="absolute bottom-full left-0 right-0 mb-2 mx-4 bg-white dark:bg-gray-700 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 overflow-hidden z-50">
              {/* Profile link */}
              <Link
                to="/admin/profile"
                onClick={() => setUserMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <User className="h-4 w-4" />
                <span className="text-sm font-medium">
                  {location.pathname === '/admin/profile' ? 'Rediger profil' : 'Vis profil'}
                </span>
              </Link>
              {/* Profile information */}
              <div className="px-4 py-3 space-y-2 border-t border-gray-100 dark:border-gray-600">
                <div className="flex items-center gap-3 text-gray-700 dark:text-gray-200">
                  <User className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                  <span className="text-sm font-medium truncate">{profile?.fullName || 'Ikke angitt'}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-600 dark:text-gray-300">
                  <svg className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm truncate">{user?.email || 'Ikke angitt'}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-600 dark:text-gray-300">
                  <svg className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span className="text-sm truncate">{profile?.phone || 'Ikke angitt'}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-600 dark:text-gray-300">
                  <UserCog className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                  <span className="text-sm font-medium capitalize">{profile?.role?.replace('_', ' ') || 'Superadmin'}</span>
                </div>
              </div>
              {/* Superadmin-only links */}
              {isSuperadminUser && (
                <>
                  <Link
                    to="/admin/superadmin"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors border-t border-gray-100 dark:border-gray-600"
                  >
                    <Shield className="h-4 w-4" />
                    <span className="text-sm font-medium">Superadmin brukere</span>
                  </Link>
                  <Link
                    to="/admin/editors"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors border-t border-gray-100 dark:border-gray-600"
                  >
                    <Edit3 className="h-4 w-4" />
                    <span className="text-sm font-medium">Editorer</span>
                  </Link>
                  <Link
                    to="/admin/import-export"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors border-t border-gray-100 dark:border-gray-600"
                  >
                    <FileDown className="h-4 w-4" />
                    <span className="text-sm font-medium">Import/Eksport</span>
                  </Link>
                </>
              )}
            </div>
          )}
          
          <div className={`flex items-center gap-2 ${sidebarOpen ? '' : 'justify-center flex-col'}`}>
            {sidebarOpen && (
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex-1 flex items-center gap-2 p-2 -m-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
              >
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {profile?.fullName || user?.email || 'User'}
                  </p>
                  {profile?.role && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{profile.role.replace('_', ' ')}</p>
                  )}
                  {!profile && (
                    <p className="text-xs text-yellow-600 dark:text-yellow-400">Profil ikke lastet</p>
                  )}
                </div>
                <ChevronUp className={`h-4 w-4 text-gray-400 dark:text-gray-500 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
              </button>
            )}
            <button
              onClick={handleSignOut}
              className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Logg ut"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 md:gap-4">
              {/* Mobile hamburger */}
              <button
                onClick={() => setMobileOpen(true)}
                className="md:hidden p-1.5 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex-shrink-0"
              >
                <Menu className="h-5 w-5" />
              </button>
              {backTo && (
                <>
                  <button
                    onClick={() => navigate(backTo)}
                    className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                  >
                    <ArrowLeft className="h-5 w-5" />
                    <span className="text-sm font-medium">Tilbake</span>
                  </button>
                  <div className="h-6 w-px bg-gray-300 dark:bg-gray-600" />
                </>
              )}
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {pageTitle || (location.pathname === '/admin/profile' ? 'Min profil' : filteredNavigation.find(item => isActive(item.href))?.name || 'Admin')}
                </h1>
                {pageSubtitle && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">{pageSubtitle}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <ThemeToggle />
              {headerAction}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900">
          {profileWarning}
          <Outlet context={{ setHeaderAction, setPageTitle, setPageSubtitle, setBackTo }} />
          <StudioXCredit />
        </main>
      </div>
    </div>
  );
}